import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  Megaphone,
  Users,
  DollarSign,
  Target,
  TrendingUp,
  Clock,
  Award,
  AlertTriangle,
  Sparkles,
  Loader2,
  Trophy,
  Crown,
  CircleDot,
  Flame,
  Thermometer,
  Snowflake,
  Calendar,
  ThumbsUp,
  ThumbsDown,
  Hourglass,
  ArrowRight,
  Percent,
  Mail,
  Phone,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip";

/**
 * Per-campaign Deep Report — answers "what was good and bad about this
 * campaign, how much was spent, how many leads came in, how many were good
 * vs bad by stage progression (not just won/lost)".
 *
 * Endpoint: GET /api/marketing-analytics/campaign-report?campaign=&date_from=&date_to=
 */

const ALLOWED_ROLES = new Set([
  "admin", "sales_director", "crm_manager", "sales", "pm_lead",
]);

function isoNDaysAgo(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}
function isoToday() { return new Date().toISOString().slice(0, 10); }
function fmtUsd(n) {
  const v = Number(n) || 0;
  const dec = Math.abs(v) >= 1000 ? 0 : 2;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
}
function fmtInt(n) { return (Number(n) || 0).toLocaleString(); }
function fmtPct(p) {
  if (p === null || p === undefined) return "—";
  return `${((Number(p) || 0) * 100).toFixed(1)}%`;
}
function fmtDays(d) {
  if (d === null || d === undefined) return "—";
  if (d < 1) return `${(d * 24).toFixed(0)}h`;
  if (d < 30) return `${Number(d).toFixed(1)}d`;
  return `${Number(d / 30).toFixed(1)}mo`;
}
function fmtDate(s) {
  if (!s) return "—";
  // UTC forced so dates match the backend's UTC-bounded range filter — a
  // lead created at 23:30 UTC otherwise reads as the next day for users
  // east of UTC and falls outside the visible period.
  try {
    return new Date(s).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
    });
  } catch { return s; }
}

const CLASS_META = {
  hot: { label: "Hot", color: "from-red-500 to-orange-500", text: "text-orange-300", border: "border-orange-500/40", bg: "bg-orange-500/15", icon: Flame },
  warm: { label: "Warm", color: "from-amber-500 to-yellow-400", text: "text-amber-300", border: "border-amber-500/40", bg: "bg-amber-500/15", icon: Thermometer },
  qualified: { label: "Qualified", color: "from-cyan-500 to-blue-500", text: "text-cyan-300", border: "border-cyan-500/40", bg: "bg-cyan-500/15", icon: Target },
  new: { label: "New", color: "from-blue-400 to-violet-500", text: "text-blue-300", border: "border-blue-500/40", bg: "bg-blue-500/15", icon: CircleDot },
  won: { label: "Won", color: "from-emerald-500 to-green-400", text: "text-emerald-300", border: "border-emerald-500/40", bg: "bg-emerald-500/15", icon: Trophy },
  lost: { label: "Lost", color: "from-red-600 to-rose-500", text: "text-red-300", border: "border-red-500/40", bg: "bg-red-500/15", icon: AlertTriangle },
  dead: { label: "Dead", color: "from-zinc-600 to-zinc-500", text: "text-zinc-400", border: "border-zinc-600/40", bg: "bg-zinc-700/30", icon: Snowflake },
};

const GRADE_META = {
  A: { cls: "bg-emerald-500/25 text-emerald-200 border-emerald-400/50", label: "A — Scale this" },
  B: { cls: "bg-cyan-500/25 text-cyan-200 border-cyan-400/50", label: "B — Maintain" },
  C: { cls: "bg-amber-500/25 text-amber-200 border-amber-400/50", label: "C — Optimize" },
  D: { cls: "bg-orange-500/25 text-orange-200 border-orange-400/50", label: "D — Re-think" },
  F: { cls: "bg-red-500/25 text-red-200 border-red-400/50", label: "F — Pause" },
};

const STAGE_TIER_CLS = (probability, isClosed) => {
  if (isClosed && probability >= 1) return "from-emerald-500 to-green-400";
  if (isClosed) return "from-red-500 to-rose-500";
  if (probability >= 0.9) return "from-emerald-500 to-cyan-400";
  if (probability >= 0.7) return "from-orange-500 to-amber-400";
  if (probability >= 0.4) return "from-amber-500 to-yellow-400";
  if (probability >= 0.15) return "from-cyan-500 to-blue-500";
  return "from-blue-500 to-violet-500";
};

function HeroMetric({ label, value, sub, icon: Icon, color = "blue", large = false }) {
  const palette = {
    blue: "from-blue-500/15 to-blue-500/5 border-blue-500/30",
    emerald: "from-emerald-500/15 to-emerald-500/5 border-emerald-500/30",
    amber: "from-amber-500/15 to-amber-500/5 border-amber-500/30",
    cyan: "from-cyan-500/15 to-cyan-500/5 border-cyan-500/30",
    red: "from-red-500/15 to-red-500/5 border-red-500/30",
    violet: "from-violet-500/15 to-violet-500/5 border-violet-500/30",
    orange: "from-orange-500/15 to-orange-500/5 border-orange-500/30",
    rose: "from-rose-500/15 to-rose-500/5 border-rose-500/30",
  }[color] || "from-zinc-500/10 to-zinc-500/5 border-zinc-700";
  return (
    <Card className={`bg-gradient-to-br ${palette} border`}>
      <CardContent className={large ? "p-5" : "p-4"}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">{label}</p>
            <p className={`${large ? "text-3xl" : "text-2xl"} font-bold text-zinc-50 truncate`}>{value}</p>
            {sub && <p className="text-xs text-zinc-500 mt-1 truncate">{sub}</p>}
          </div>
          {Icon && <Icon className={`${large ? "w-6 h-6" : "w-5 h-5"} text-zinc-400 flex-shrink-0 mt-0.5`} />}
        </div>
      </CardContent>
    </Card>
  );
}

function ClassificationStrip({ classification }) {
  const order = ["hot", "warm", "qualified", "new", "won", "lost", "dead"];
  const entries = order.map((k) => ({ k, n: classification?.[k] || 0 })).filter((e) => e.n > 0);
  const sum = entries.reduce((a, e) => a + e.n, 0) || 1;
  return (
    <Card className="bg-zinc-900/40 border-zinc-800">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-violet-400" />
            <span className="text-sm font-medium text-zinc-50">Lead quality breakdown</span>
            <span className="text-xs text-zinc-500">(by stage progression — independent of won/lost)</span>
          </div>
          <span className="text-xs text-zinc-400">{sum} total</span>
        </div>
        <div className="flex h-10 rounded-lg overflow-hidden border border-zinc-800">
          {entries.map(({ k, n }) => {
            const meta = CLASS_META[k];
            const pct = (n / sum) * 100;
            return (
              <Tooltip key={k}>
                <TooltipTrigger asChild>
                  <div
                    className={`bg-gradient-to-r ${meta.color} h-full flex items-center justify-center text-sm font-bold text-white cursor-default hover:brightness-110 transition`}
                    style={{ width: `${pct}%` }}
                  >
                    {pct >= 5 ? n : ""}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-zinc-950 border-zinc-700">
                  <div className="text-zinc-50 font-semibold">{meta.label}: {n} ({pct.toFixed(1)}%)</div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-2">
          {order.map((k) => {
            const meta = CLASS_META[k];
            const Icon = meta.icon;
            const n = classification?.[k] || 0;
            return (
              <div
                key={k}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border ${meta.border} ${meta.bg} text-xs ${meta.text}`}
              >
                <Icon className="w-3 h-3" />
                <span className="font-medium">{meta.label}</span>
                <span className="ml-1 font-bold text-zinc-50">{n}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function FunnelVis({ stages }) {
  const valid = (stages || []).filter((s) => s.reached_count >= 0);
  if (!valid.length) return <p className="text-sm text-zinc-500 p-4">No stage data — leads in this date range haven't entered any pipeline yet.</p>;
  const maxReached = Math.max(...valid.map((s) => s.reached_count), 1);
  return (
    <div className="space-y-1">
      {valid.map((s, idx) => {
        const wPct = Math.max(8, (s.reached_count / maxReached) * 100);
        const cls = STAGE_TIER_CLS(s.probability, s.is_closed);
        return (
          <div key={s.stage_id} className="group">
            <div className="flex items-center gap-3 mb-1">
              <div className="flex-shrink-0 w-56 text-sm text-zinc-200 truncate" title={s.label}>
                <span className="text-zinc-500 text-xs mr-1">#{idx + 1}</span> {s.label}
              </div>
              <div className="flex-1 relative h-9 bg-zinc-950/60 rounded-md border border-zinc-800/50 overflow-hidden">
                <div className={`bg-gradient-to-r ${cls} h-full flex items-center px-3 rounded-md text-white`} style={{ width: `${wPct}%` }}>
                  <span className="font-bold text-sm">{s.reached_count}</span>
                  <span className="ml-2 text-[10px] uppercase tracking-wider opacity-80">{Math.round((s.probability || 0) * 100)}%</span>
                </div>
                {s.current_count > 0 && (
                  <div className="absolute top-0 right-2 h-full flex items-center text-[10px] text-zinc-300">
                    <CircleDot className="w-3 h-3 text-violet-300 mr-1" /> {s.current_count} now
                  </div>
                )}
              </div>
              <div className="flex-shrink-0 w-28 text-right text-xs">
                {s.avg_days_to_reach !== null && (
                  <div className="text-zinc-400">
                    <Clock className="w-3 h-3 inline mr-1" /> {fmtDays(s.avg_days_to_reach)}
                  </div>
                )}
                {s.avg_days_in_stage !== null && (
                  <div className="text-zinc-500 text-[11px]">
                    <Hourglass className="w-3 h-3 inline mr-1" /> {fmtDays(s.avg_days_in_stage)} in
                  </div>
                )}
              </div>
            </div>
            {s.drop_off_pct !== null && s.drop_off_pct > 0 && (
              <div className="ml-56 pl-3 -mt-0.5 mb-1 text-[10px] text-red-400/80 flex items-center gap-1">
                <ArrowRight className="w-3 h-3" /> Lost {s.drop_off_from_prev} ({s.drop_off_pct}%)
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SignalList({ items, kind }) {
  if (!items?.length) return null;
  const isGood = kind === "good";
  const Icon = isGood ? ThumbsUp : ThumbsDown;
  const cls = isGood
    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
    : "bg-red-500/10 border-red-500/30 text-red-200";
  return (
    <Card className={`border ${cls}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-zinc-50">
          <Icon className={`w-4 h-4 ${isGood ? "text-emerald-400" : "text-red-400"}`} />
          {isGood ? "What's working" : "What's not working"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5 pt-0">
        {items.map((s, i) => (
          <div key={i} className="text-xs flex items-start gap-2">
            <span className={`mt-1 w-1 h-1 rounded-full flex-shrink-0 ${isGood ? "bg-emerald-400" : "bg-red-400"}`}></span>
            <span>{s}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function VerdictCard({ verdict, onGenerate, generating }) {
  if (!verdict) {
    return (
      <Card className="bg-gradient-to-br from-violet-900/20 to-purple-900/10 border-violet-500/30">
        <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-violet-400" />
            <div>
              <p className="text-sm font-medium text-zinc-50">No AI verdict yet</p>
              <p className="text-xs text-zinc-400">Generate an A-F grade + scaling recommendation + recommendations for agency and sales (~$0.001).</p>
            </div>
          </div>
          <Button onClick={onGenerate} disabled={generating} className="bg-violet-600 hover:bg-violet-500">
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Generate AI verdict
          </Button>
        </CardContent>
      </Card>
    );
  }
  const grade = verdict.campaign_grade || "C";
  const meta = GRADE_META[grade] || GRADE_META.C;
  return (
    <Card className="bg-gradient-to-br from-violet-900/20 to-purple-900/10 border-violet-500/30">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-violet-400" />
            <span className={`px-3 py-1.5 rounded-lg border text-base font-bold ${meta.cls}`}>{meta.label}</span>
            <span className="px-2.5 py-1 rounded-md bg-zinc-800/50 border border-zinc-700 text-xs text-zinc-300">
              {verdict.scaling_recommendation || "—"}
            </span>
          </div>
          <Button size="sm" variant="outline" onClick={onGenerate} disabled={generating} className="border-violet-500/50 text-violet-300 hover:bg-violet-900/30">
            {generating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Regenerate
          </Button>
        </div>
        {verdict.summary_ru && (
          <p className="text-sm text-zinc-200 leading-relaxed">{verdict.summary_ru}</p>
        )}
        {(verdict.key_strengths?.length || verdict.key_weaknesses?.length) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {verdict.key_strengths?.length > 0 && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 space-y-1.5">
                <p className="text-xs font-medium text-emerald-300 uppercase tracking-wide">Strengths</p>
                {verdict.key_strengths.map((s, i) => (
                  <div key={i} className="text-xs text-zinc-200 flex items-start gap-2">
                    <ThumbsUp className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            )}
            {verdict.key_weaknesses?.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 space-y-1.5">
                <p className="text-xs font-medium text-red-300 uppercase tracking-wide">Weaknesses</p>
                {verdict.key_weaknesses.map((s, i) => (
                  <div key={i} className="text-xs text-zinc-200 flex items-start gap-2">
                    <ThumbsDown className="w-3 h-3 text-red-400 mt-0.5 flex-shrink-0" />
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {(verdict.recommendations_for_agency?.length || verdict.recommendations_for_sales?.length) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {verdict.recommendations_for_agency?.length > 0 && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-xs font-medium text-blue-300 uppercase tracking-wide mb-1.5">For marketing agency</p>
                <ul className="space-y-1 text-xs text-zinc-200 list-disc list-inside">
                  {verdict.recommendations_for_agency.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            {verdict.recommendations_for_sales?.length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-xs font-medium text-amber-300 uppercase tracking-wide mb-1.5">For sales team</p>
                <ul className="space-y-1 text-xs text-zinc-200 list-disc list-inside">
                  {verdict.recommendations_for_sales.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
        {verdict.manager_observations && (
          <div className="bg-zinc-800/30 border border-zinc-700 rounded-lg p-3">
            <p className="text-xs font-medium text-zinc-400 uppercase tracking-wide mb-1">Manager activity</p>
            <p className="text-xs text-zinc-200">{verdict.manager_observations}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DailySpendChart({ daily }) {
  if (!daily?.length) return null;
  const maxSpend = Math.max(...daily.map((d) => d.spend_usd || 0), 1);
  return (
    <Card className="bg-zinc-900/40 border-zinc-800">
      <CardHeader className="pb-2">
        <CardTitle className="text-zinc-50 text-sm flex items-center gap-2">
          <Calendar className="w-4 h-4 text-cyan-400" />
          Daily spend (Meta Ads)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-0.5 h-24">
          {daily.map((d, i) => {
            const h = Math.max(2, ((d.spend_usd || 0) / maxSpend) * 100);
            return (
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                  <div
                    className="flex-1 bg-gradient-to-t from-cyan-500/50 to-cyan-400/80 rounded-sm cursor-default hover:from-cyan-400 hover:to-cyan-300 transition min-w-[2px]"
                    style={{ height: `${h}%` }}
                  ></div>
                </TooltipTrigger>
                <TooltipContent className="bg-zinc-950 border-zinc-700 text-zinc-200">
                  <div>{fmtDate(d.date)}</div>
                  <div className="font-semibold">{fmtUsd(d.spend_usd)}</div>
                  <div className="text-xs text-zinc-400">{d.action_lead || 0} leads · {d.clicks || 0} clicks</div>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        <div className="flex justify-between mt-1 text-[10px] text-zinc-500">
          <span>{fmtDate(daily[0].date)}</span>
          <span>{fmtDate(daily[daily.length - 1].date)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function LeadRow({ lead }) {
  const [open, setOpen] = useState(false);
  const c = lead.contact || {};
  const d = lead.deal;
  const ai = lead.ai_summary;
  const stage = d?.stage_status || "open";
  const tier = ai?.quality_tier;
  const tierCls = {
    premium: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
    quality: "bg-cyan-500/20 text-cyan-200 border-cyan-500/40",
    average: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40",
    weak: "bg-amber-500/20 text-amber-200 border-amber-500/40",
    junk: "bg-red-500/25 text-red-200 border-red-500/40",
  }[tier] || "";
  const stageCls = {
    won: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    lost: "bg-red-500/15 text-red-300 border-red-500/30",
    open: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  }[stage] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/30";
  return (
    <div className="border-b border-zinc-800/50">
      <div
        className="flex items-center gap-3 py-2 px-2 hover:bg-zinc-800/30 cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        {open ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-zinc-50 truncate max-w-md">{c.name || c.email || "Unknown"}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${stageCls}`}>{d?.stage_label || stage}</span>
            {tier && <span className={`text-[10px] px-1.5 py-0.5 rounded border ${tierCls}`}>{tier} · {ai?.quality_score}</span>}
            {c.country && <span className="text-xs text-zinc-500">{c.country}</span>}
          </div>
        </div>
        <div className="text-xs text-zinc-400 flex items-center gap-3 flex-shrink-0">
          {d?.amount_usd > 0 && <span className="text-zinc-300 font-medium">{fmtUsd(d.amount_usd)}</span>}
          {lead.owner?.name && <span className="text-zinc-500">{lead.owner.name}</span>}
        </div>
      </div>
      {open && (
        <div className="px-8 pb-3 pt-1 bg-zinc-900/30 text-xs space-y-2">
          <div className="flex flex-wrap gap-3 text-zinc-400">
            {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /><a className="hover:text-zinc-200" href={`mailto:${c.email}`}>{c.email}</a></span>}
            {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /><a className="hover:text-zinc-200" href={`tel:${c.phone}`}>{c.phone}</a></span>}
            {c.company && <span>· {c.company}</span>}
          </div>
          {ai?.summary_ru && (
            <div className="bg-zinc-800/30 border border-zinc-700/50 rounded p-2 text-zinc-200">
              {ai.summary_ru}
            </div>
          )}
          {ai?.recommended_action && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2 text-emerald-200">
              <span className="font-medium">Next action:</span> {ai.recommended_action}
            </div>
          )}
          {d?.lost_reason && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-2 text-red-200">
              <span className="font-medium">Lost reason:</span> {d.lost_reason}
            </div>
          )}
          {d?.description_excerpt && (
            <div className="text-zinc-500 italic whitespace-pre-wrap line-clamp-4">{d.description_excerpt}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MarketingCampaignReportPage() {
  const { campaign: campaignParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [accessAllowed, setAccessAllowed] = useState(null);

  const initialFrom = searchParams.get("date_from") || isoNDaysAgo(60);
  const initialTo = searchParams.get("date_to") || isoToday();

  const [dateFrom, setDateFrom] = useState(initialFrom);
  const [dateTo, setDateTo] = useState(initialTo);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingVerdict, setGeneratingVerdict] = useState(false);
  const [showAllLeads, setShowAllLeads] = useState(false);

  const campaign = decodeURIComponent(campaignParam || "");

  useEffect(() => {
    let cancelled = false;
    // Quick short-circuits before falling back to API access check:
    // role match OR marketing-agency allowlist flag (set by /auth/me).
    if (user && (ALLOWED_ROLES.has(user.role) || user.marketing_agency_user === true)) { setAccessAllowed(true); return; }
    api.get("/marketing-analytics/access").then((r) => {
      if (!cancelled) setAccessAllowed(!!r.data?.can_view);
    }).catch(() => { if (!cancelled) setAccessAllowed(false); });
    return () => { cancelled = true; };
  }, [user]);

  const fetchReport = async () => {
    if (!campaign) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/marketing-analytics/campaign-report", {
        params: { campaign, date_from: dateFrom, date_to: dateTo },
      });
      setData(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to load campaign report";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessAllowed) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessAllowed, campaign]);

  const generateVerdict = async (force = false) => {
    setGeneratingVerdict(true);
    try {
      const res = await api.post("/marketing-analytics/campaign-verdict", null, {
        params: { campaign, date_from: dateFrom, date_to: dateTo, force },
      });
      if (res.data) {
        setData((prev) => ({ ...prev, verdict: res.data }));
        toast.success("AI verdict generated");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate verdict");
    } finally {
      setGeneratingVerdict(false);
    }
  };

  if (accessAllowed === null) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;
  }
  if (!accessAllowed) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6 flex items-center gap-3 text-amber-400">
            <AlertTriangle className="w-5 h-5" />
            <span>This report is only available to admin, sales_director, crm_manager, sales, and pm_lead.</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  const meta = data?.meta;
  const perf = data?.performance || {};
  const k = data?.kpis || {};
  const verdict = data?.verdict;
  const leadsToShow = showAllLeads ? (data?.leads || []) : (data?.leads || []).slice(0, 15);

  return (
    <TooltipProvider delayDuration={150}>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Back nav */}
        <div className="flex items-center gap-2 text-sm">
          <Link to="/marketing-analytics/funnel" className="text-zinc-400 hover:text-zinc-200 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to funnel report
          </Link>
        </div>

        {/* Hero header */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-amber-400" />
            <h1 className="text-2xl font-bold text-zinc-50">Campaign Deep Report</h1>
          </div>
          <p className="text-xl text-zinc-300 truncate">{campaign}</p>
          <div className="flex items-end gap-2 flex-wrap pt-2">
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-zinc-800 border-zinc-700 text-sm w-40" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-zinc-400">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-zinc-800 border-zinc-700 text-sm w-40" />
            </div>
            <Button onClick={fetchReport} disabled={loading} className="bg-blue-600 hover:bg-blue-500">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Apply
            </Button>
          </div>
        </div>

        {error && (
          <Card className="bg-red-900/20 border-red-800">
            <CardContent className="p-4 text-sm text-red-300">{error}</CardContent>
          </Card>
        )}

        {/* Campaign window banner */}
        <Card className="bg-gradient-to-r from-blue-900/30 to-cyan-900/20 border-blue-500/30">
          <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-cyan-400" />
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-400">Campaign delivery window</p>
                <p className="text-lg font-bold text-zinc-50">
                  {meta?.has_data
                    ? <>{fmtDate(meta.start)} → {fmtDate(meta.end)} <span className="text-sm font-normal text-zinc-400">· {meta.active_days} active day{meta.active_days !== 1 ? "s" : ""}</span></>
                    : <span className="text-zinc-500 text-base">No Meta Ads data — check that UTM tagging matches Ads Manager campaign name</span>}
                </p>
              </div>
            </div>
            {meta?.has_data && (
              <div className="flex gap-4 text-sm">
                <div><span className="text-zinc-500 text-xs">Spend:</span> <span className="font-bold text-emerald-300">{fmtUsd(meta.spend_usd)}</span></div>
                <div><span className="text-zinc-500 text-xs">CTR:</span> <span className="font-bold text-cyan-300">{meta.ctr_pct}%</span></div>
                <div><span className="text-zinc-500 text-xs">CPC:</span> <span className="font-bold text-amber-300">{fmtUsd(meta.cpc_usd)}</span></div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hero metrics row */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <HeroMetric icon={DollarSign} label="Spend" value={fmtUsd(perf.spend_usd)} color="emerald" sub={meta?.active_days ? `${meta.active_days}d active` : ""} />
          <HeroMetric icon={Users} label="Leads (CRM)" value={fmtInt(perf.leads_hub)} color="blue" sub={`${fmtInt(perf.leads_meta)} via Meta pixel`} />
          <HeroMetric icon={Target} label="Qualified" value={fmtInt(perf.qualified)} color="cyan" sub={fmtPct(perf.qualification_rate)} />
          <HeroMetric icon={Flame} label="Hot leads" value={fmtInt(perf.hot)} color="orange" sub="Reached proposal+" />
          <HeroMetric icon={Trophy} label="Won" value={fmtInt(perf.won)} color="emerald" sub={fmtUsd(perf.won_value_usd)} />
          <HeroMetric
            icon={Percent} label="CPL (Hub)"
            value={perf.cpl_hub_usd !== null ? fmtUsd(perf.cpl_hub_usd) : "—"}
            color="amber"
            sub={perf.cpl_meta_usd !== null ? `Meta CPL: ${fmtUsd(perf.cpl_meta_usd)}` : ""}
          />
        </div>

        {/* Secondary metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <HeroMetric icon={Crown} label="CAC" value={perf.cac_usd !== null ? fmtUsd(perf.cac_usd) : "—"} color="violet" sub="Per won deal" />
          <HeroMetric icon={TrendingUp} label="ROAS" value={perf.roas !== null ? `${perf.roas}×` : "—"} color={perf.roas >= 1 ? "emerald" : "red"} sub="Won value / spend" />
          <HeroMetric icon={Clock} label="⌀ time to qualified" value={fmtDays(perf.avg_days_to_qualified)} color="cyan" />
          <HeroMetric icon={Hourglass} label="⌀ cycle to close" value={fmtDays(perf.avg_cycle_close_days)} color="violet" />
          <HeroMetric icon={DollarSign} label="Pipeline value" value={fmtUsd(perf.pipeline_value_usd)} color="amber" sub={`${fmtInt(k.in_pipeline_count || 0)} open`} />
        </div>

        {/* Good / bad signals */}
        {(data?.good_signals?.length || data?.bad_signals?.length) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SignalList items={data.good_signals} kind="good" />
            <SignalList items={data.bad_signals} kind="bad" />
          </div>
        )}

        {/* AI verdict */}
        <VerdictCard verdict={verdict} onGenerate={() => generateVerdict(!!verdict)} generating={generatingVerdict} />

        {/* Lead classification */}
        <ClassificationStrip classification={data?.lead_classification} />

        {/* Daily spend */}
        {meta?.has_data && <DailySpendChart daily={meta.daily} />}

        {/* Funnel */}
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-50 text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-cyan-400" />
              Pipeline funnel for this campaign
            </CardTitle>
            <p className="text-xs text-zinc-500">
              How leads from this campaign progressed through the CRM pipeline.
            </p>
          </CardHeader>
          <CardContent>
            <FunnelVis stages={data?.stages || []} />
          </CardContent>
        </Card>

        {/* Manager performance */}
        {data?.managers?.length > 0 && (
          <Card className="bg-zinc-900/40 border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-zinc-50 text-base flex items-center gap-2">
                <Crown className="w-4 h-4 text-violet-400" />
                Manager performance on this campaign
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-zinc-500 border-b border-zinc-800">
                  <tr>
                    <th className="text-left py-2 px-2">Manager</th>
                    <th className="text-right py-2 px-2">Leads</th>
                    <th className="text-right py-2 px-2 text-orange-400">Hot</th>
                    <th className="text-right py-2 px-2 text-amber-400">Warm</th>
                    <th className="text-right py-2 px-2 text-cyan-400">Qual.</th>
                    <th className="text-right py-2 px-2 text-emerald-400">Won</th>
                    <th className="text-right py-2 px-2 text-red-400">Lost</th>
                    <th className="text-right py-2 px-2">Qual. %</th>
                    <th className="text-right py-2 px-2">⌀ first response</th>
                    <th className="text-right py-2 px-2">Pipeline $</th>
                  </tr>
                </thead>
                <tbody>
                  {data.managers.map((m) => (
                    <tr key={m.id} className="border-b border-zinc-800/40 hover:bg-zinc-800/20">
                      <td className="py-2 px-2 text-zinc-200 font-medium">{m.name || m.id}</td>
                      <td className="py-2 px-2 text-right text-zinc-50 font-bold">{m.leads}</td>
                      <td className="py-2 px-2 text-right text-orange-300">{m.hot || "—"}</td>
                      <td className="py-2 px-2 text-right text-amber-300">{m.warm || "—"}</td>
                      <td className="py-2 px-2 text-right text-cyan-300">{m.qualified || "—"}</td>
                      <td className="py-2 px-2 text-right text-emerald-300 font-medium">{m.won || "—"}</td>
                      <td className="py-2 px-2 text-right text-red-300">{m.lost || "—"}</td>
                      <td className="py-2 px-2 text-right text-zinc-300">{fmtPct(m.qualification_rate)}</td>
                      <td className="py-2 px-2 text-right text-zinc-400">{fmtDays(m.avg_first_response_days)}</td>
                      <td className="py-2 px-2 text-right text-zinc-300">{fmtUsd(m.pipeline_value_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Lead list */}
        {data?.leads?.length > 0 && (
          <Card className="bg-zinc-900/40 border-zinc-800">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-zinc-50 text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-400" />
                Lead-level detail ({data.leads.length})
              </CardTitle>
              {data.leads.length > 15 && (
                <Button size="sm" variant="outline" onClick={() => setShowAllLeads(!showAllLeads)} className="border-zinc-700 text-zinc-300">
                  {showAllLeads ? "Collapse" : `Show all ${data.leads.length}`}
                </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {leadsToShow.map((lead, i) => <LeadRow key={lead.contact?.crm_id || i} lead={lead} />)}
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
}
